/**
 * ADMIN-T012 — vue-router v4 assembly with strongly-typed route meta.
 * Route lifecycle telemetry hooks (T014) attached here as well.
 */

import {
  type NavigationGuardNext,
  type RouteLocationNormalized,
  type RouteRecordRaw,
  createRouter,
  createWebHistory,
} from 'vue-router';
import { adminRoutes } from './routes/admin';
import { dashboardRoutes } from './routes/dashboard';
import { authRoutes } from './routes/auth';
import { errorRoutes } from './routes/error';
import { emitRoutePhase } from '@/observability/routePhases';
import { usePermissionStore } from '@/permission/store';

export interface AppRouteMeta {
  /** Permission code required to view this route. `null` = public. */
  permission: string | null;
  /** Authenticated route that intentionally has no business permission gate. */
  requiresAuth?: boolean;
  /** Data scope expected for this route (e.g. 'region' | 'project' | 'global'). */
  data_scope: 'region' | 'project' | 'global' | 'self' | null;
  /** Chunk name hint for code splitting. */
  lazy_chunk_name?: string;
  /** Optional list of API endpoints to prime during DataPriming phase. */
  primingRequests?: string[];
  /** Display title (zh-CN). */
  title?: string;
  /** Whether the route lives inside the read-only dashboard module. */
  readOnly?: boolean;
}

declare module 'vue-router' {
  // Augment the framework's RouteMeta with our strict shape.
  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  interface RouteMeta extends AppRouteMeta {}
}

const routes: RouteRecordRaw[] = [
  ...authRoutes,
  ...adminRoutes,
  ...dashboardRoutes,
  ...errorRoutes,
];

export const router = createRouter({
  history: createWebHistory(),
  routes,
});

/**
 * ADMIN-T017 / ADMIN-T357 — the permission navigation guard. Exported so it can be
 * unit-tested directly (the production gate logic, not a re-implementation). Runs on
 * every navigation, INCLUDING direct-URL entry, so a deep-linked route the account
 * lacks is sent to the denied page (not silently rendered) — gating is not UI-only.
 */
export async function permissionGuard(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext,
): Promise<void> {
  emitRoutePhase('RouteResolving', { path: to.fullPath, from: from.fullPath });
  emitRoutePhase('RouteGuarding', { path: to.fullPath });

  const store = usePermissionStore();
  const requiredPermission = to.meta.permission;

  // Already-authenticated users shouldn't see the login page.
  if (to.name === 'auth.login' && store.isAuthenticated) {
    return next('/admin');
  }

  // Public routes pass through. `requiresAuth` supports a common landing page
  // without coupling it to an unrelated business permission.
  if ((requiredPermission === null || requiredPermission === undefined) && !to.meta.requiresAuth) {
    return next();
  }

  // Unauthenticated → redirect to login, preserving the intended path.
  if (!store.isAuthenticated) {
    return next({ path: '/login', query: { redirect: to.fullPath } });
  }

  if (requiredPermission === null || requiredPermission === undefined) {
    return next();
  }

  // Authenticated but missing the required permission → denied page.
  if (!store.hasPermission(requiredPermission)) {
    return next({ name: 'error.denied' });
  }

  next();
}

router.beforeEach(permissionGuard);

router.beforeResolve(async (to) => {
  emitRoutePhase('ChunkLoading', { path: to.fullPath });
  emitRoutePhase('DataPriming', { path: to.fullPath, requests: to.meta.primingRequests ?? [] });
});

router.afterEach((to, _from, failure) => {
  if (failure) {
    emitRoutePhase('ChunkLoadFailed', { path: to.fullPath, reason: String(failure) });
    return;
  }
  emitRoutePhase('Interactive', { path: to.fullPath });
  emitRoutePhase('RouteSucceeded', { path: to.fullPath });
});

router.onError((err, to) => {
  emitRoutePhase('ChunkLoadFailed', { path: to?.fullPath ?? '', reason: String(err) });
});
